// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { mergeWithCss } from "@steiner/XDL/Utilities";
import React, { Children, FC, PropsWithChildren, useState } from "react";

export interface CartLineContainerProps {
    extendedStyles?: CartLineContainerStyles;
    [key: string]: any;
}

interface OwnProps {
    Image: FC<PropsWithChildren>;
    Description: FC<PropsWithChildren>;
    Price: FC<PropsWithChildren>;
    Qty: FC<PropsWithChildren>;
    QtyShipped: FC<PropsWithChildren>;
    Total: FC<PropsWithChildren>;
    Remove: FC<PropsWithChildren>;
    Notes: FC<PropsWithChildren>;
    Actions: FC<PropsWithChildren>;
    Inventory: FC<PropsWithChildren>;
}

export interface CartLineContainerStyles {
    container?: InjectableCss;
    image?: InjectableCss;
    description?: InjectableCss;
    price?: InjectableCss;
    qty?: InjectableCss;
    qtyShipped?: InjectableCss;
    total?: InjectableCss;
    notes?: InjectableCss;
    mobileNotes?: InjectableCss;
    remove?: InjectableCss;
    actions?: InjectableCss;
    inventory?: InjectableCss;
}

export const cartLineContainerStyles: CartLineContainerStyles = {};

const names = {
    Image: "Image",
    Description: "Description",
    Price: "Price",
    Qty: "Qty",
    QtyShipped: "QtyShipped",
    Total: "Total",
    Remove: "Remove",
    Notes: "Notes",
    Actions: "Actions",
    Inventory: "Inventory",
};

const Image: FC = () => null;
const Description: FC = () => null;
const Price: FC = () => null;
const Qty: FC = () => null;
const QtyShipped: FC = () => null;
const Total: FC = () => null;
const Remove: FC = () => null;
const Notes: FC = () => null;
const Actions: FC = () => null;
const Inventory: FC = () => null;

const CartLineContainer: React.FC<PropsWithChildren<CartLineContainerProps>> & OwnProps = ({
    children,
    extendedStyles,
    ...otherProps
}) => {
    const [styles] = useState(() => mergeWithCss(cartLineContainerStyles, extendedStyles));

    const { Image, Description, Price, Qty, QtyShipped, Total, Remove, Notes, Actions, Inventory } =
        React.useMemo(() => {
            const childrenArray = Children.toArray(children) as React.ReactElement<PropsWithChildren>[];
            const MappedChildren = childrenArray.reduce(
                (results, current) => {
                    const currentName = (current.type as React.NamedExoticComponent<any>)?.displayName;
                    if (currentName) {
                        results[currentName] = current;
                    }
                    return results;
                },
                {} as Record<string, React.ReactElement<PropsWithChildren>>,
            );
            const slots = Object.entries(names).reduce(
                (results, [key, value], idx) => {
                    results[value] = MappedChildren[value] || null;
                    return results;
                },
                {} as Record<string, React.ReactElement<PropsWithChildren> | null>,
            );

            return slots;
        }, [children]);

    return (
        <StyledWrapper {...styles.container} {...otherProps}>
            <StyledWrapper {...styles.image} data-test-selector="cart-image-section">
                {Image?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.description} data-test-selector="cart-description-section" {...otherProps}>
                {Description?.props?.children}
                <StyledWrapper {...styles.notes} data-test-selector="cart-notes-section">
                    {Notes?.props?.children}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.inventory} data-test-selector="cart-inventory-section" {...otherProps}>
                {Inventory?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.price} data-test-selector="cart-price-section" {...otherProps}>
                {Price?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.qty} data-test-selector="cart-qty-section" {...otherProps}>
                {Qty?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.qtyShipped} data-test-selector="cart-qty-shipped-section" {...otherProps}>
                {QtyShipped?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.total} data-test-selector="cart-total-section" {...otherProps}>
                {Total?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.remove} data-test-selector="cart-remove-section">
                {Remove?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.mobileNotes} data-test-selector="cart-notes-section-mobile" {...otherProps}>
                {Notes?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.actions} data-test-selector="cart-actions" {...otherProps}>
                {Actions?.props?.children}
            </StyledWrapper>
        </StyledWrapper>
    );
};

CartLineContainer.Image = Image;
CartLineContainer.Description = Description;
CartLineContainer.Price = Price;
CartLineContainer.Qty = Qty;
CartLineContainer.QtyShipped = QtyShipped;
CartLineContainer.Total = Total;
CartLineContainer.Remove = Remove;
CartLineContainer.Notes = Notes;
CartLineContainer.Actions = Actions;
CartLineContainer.Inventory = Inventory;

CartLineContainer.Image.displayName = names.Image;
CartLineContainer.Description.displayName = names.Description;
CartLineContainer.Price.displayName = names.Price;
CartLineContainer.Qty.displayName = names.Qty;
CartLineContainer.QtyShipped.displayName = names.QtyShipped;
CartLineContainer.Total.displayName = names.Total;
CartLineContainer.Remove.displayName = names.Remove;
CartLineContainer.Notes.displayName = names.Notes;
CartLineContainer.Actions.displayName = names.Actions;
CartLineContainer.Inventory.displayName = names.Inventory;

export default CartLineContainer;
